/**
 * use-scroll-lock-fallback — let a portaled popup scroll inside a Radix dialog.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Radix's Dialog locks scrolling with react-remove-scroll, which permits wheel
 * events only inside its `shards` — and the only shard is the dialog Content.
 * The hover card is portaled to <body> (it has to be: the optimizer drawer's
 * Content carries a permanent `-translate-x-1/2 -translate-y-1/2`, and a
 * `position: fixed` card inside a transformed ancestor is positioned against
 * that ancestor instead of the viewport). So the card sits outside the shard
 * and every wheel over it is preventDefault()'d — the card renders its full
 * ~1500px of metrics but will not scroll a pixel.
 *
 * The lock cancels from a document listener in the BUBBLE phase, i.e. after the
 * event has passed through the card. Stopping propagation at the card therefore
 * means the lock never sees this particular wheel, and the browser scrolls the
 * card natively — keeping the momentum/fluidity that a manual `scrollTop +=`
 * fallback would destroy. `overscroll-contain` on the card still stops the
 * scroll chaining out to the drawer behind once an edge is reached.
 *
 * Only wheels the card can actually consume are swallowed; at an edge (or when
 * there is nothing to scroll) the event is left alone so surrounding behaviour
 * is unchanged.
 */

import { useEffect, type RefObject } from 'react';

function canScroll(el: HTMLElement, deltaY: number): boolean {
  const max = el.scrollHeight - el.clientHeight;
  if (max <= 0) return false;
  if (deltaY < 0) return el.scrollTop > 0;
  if (deltaY > 0) return el.scrollTop < max;
  return false;
}

export function useScrollLockFallback(ref: RefObject<HTMLElement>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (!canScroll(el, event.deltaY)) return;
      // Keep it away from react-remove-scroll's document-level listener; the
      // browser then performs its own (smooth) scroll on this element.
      event.stopPropagation();
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ref]);
}
